import React, { useEffect, useRef, useState } from 'react';
import { audienceService } from '../../services';
import { useDialog } from '../../context/DialogContext';
import { ClubPanel, PillButton, RoundCheck, INK, MUTED, INSET, CHIP, GREEN, RED, label as lbl } from '../club/clubUi';

const FIELDS = [
  { key: 'name', label: 'FULL NAME' },
  { key: 'firstName', label: 'FIRST NAME' },
  { key: 'lastName', label: 'LAST NAME' },
  { key: 'email', label: 'EMAIL' },
  { key: 'phone', label: 'PHONE' },
];

const chip = (on) => ({ height: 30, padding: '0 12px', border: 'none', borderRadius: 999, background: on ? INK : '#fff', color: on ? '#fff' : INK, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '.06em', cursor: 'pointer' });

/**
 * IMPORT A LIST (design_handoff_club_pages/README.md §2 CONTACTS) — a CSV or Excel file a manager
 * already has. Three steps in one panel: PICK A FILE → the server reads it and guesses the
 * columns → WHICH COLUMN IS WHICH (chip rows), NUMBERS ARE +1, the preview grid, PUT THEM IN A
 * GROUP, the consent check → IMPORT N ROWS → the done state. Nothing is written until commit;
 * re-importing merges by email / phone instead of duplicating.
 */
export default function ImportSheet({ ownerType, ownerId, groups = [], onDone, onClose, mobile = false }) {
  const { showAlert } = useDialog();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mapping, setMapping] = useState({ email: null, phone: null, name: null, firstName: null, lastName: null });
  const [groupChoice, setGroupChoice] = useState('none'); // none | <groupId> | new
  const [newGroupName, setNewGroupName] = useState('');
  const [consent, setConsent] = useState(false);
  const [dial, setDial] = useState('+1');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!file) return;
    (async () => {
      setBusy(true); setPreview(null); setResult(null);
      const r = await audienceService.previewImport(ownerType, ownerId, file);
      setBusy(false);
      if (!r.success) { await showAlert({ title: 'COULD NOT READ THAT FILE', message: r.error || 'Try a CSV or an .xlsx.', type: 'error' }); setFile(null); return; }
      setPreview(r.data);
      const s = r.data.suggested || {};
      setMapping({ email: s.email ?? null, phone: s.phone ?? null, name: s.name ?? null, firstName: s.firstName ?? null, lastName: s.lastName ?? null });
      if (!newGroupName) setNewGroupName(file.name.replace(/\.[^.]+$/, '').slice(0, 60));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, ownerType, ownerId]);

  const setMap = (field, idx) => {
    const v = idx === null ? null : Number(idx);
    setMapping((m) => {
      const next = { ...m, [field]: v };
      // One column, one field.
      if (v !== null) Object.keys(next).forEach((k) => { if (k !== field && next[k] === v) next[k] = null; });
      return next;
    });
  };

  const needContact = !!preview && mapping.email === null && mapping.phone === null;
  const canCommit = preview && !needContact && consent && !busy && (groupChoice !== 'new' || newGroupName.trim());

  const commit = async () => {
    setBusy(true);
    const r = await audienceService.commitImport(ownerType, ownerId, file, {
      ...mapping, defaultDialCode: dial, consent,
      groupId: groupChoice !== 'none' && groupChoice !== 'new' ? groupChoice : null,
      newGroupName: groupChoice === 'new' ? newGroupName.trim() : null,
    });
    setBusy(false);
    if (!r.success) { await showAlert({ title: 'IMPORT FAILED', message: r.error || 'Try again.', type: 'error' }); return; }
    setResult(r.data);
    onDone?.(r.data);
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); setConsent(false); setGroupChoice('none'); };
  const mapped = new Set(Object.values(mapping).filter((v) => v != null));
  const cols = preview?.columns || [];

  return (
    <ClubPanel phone={mobile} style={{ gap: 16 }} data-testid="import-sheet">
      <span className="flex items-center justify-between" style={{ gap: 10 }}>
        <span style={lbl(12, '.16em')}>IMPORT A LIST</span>
        <PillButton variant="tertiary" size={32} onClick={onClose}>CLOSE ✕</PillButton>
      </span>

      {result ? (
        <div className="flex flex-col items-center text-center" style={{ gap: 10, padding: 28, borderRadius: 22, background: INSET }} data-testid="import-result">
          <span style={{ fontSize: 28, fontWeight: 800 }}>{Number(result.added || 0).toLocaleString()}</span>
          <span style={lbl(10, '.16em')}>ADDED TO THE LIST</span>
          <span style={{ fontSize: 12, color: MUTED }}>
            {Number(result.updated || 0).toLocaleString()} already on the list, merged · {Number(result.invalid || 0).toLocaleString()} skipped with no email or phone{result.groupName ? ` · all in ${result.groupName}` : ''}
          </span>
          <PillButton size={38} onClick={reset} style={{ marginTop: 6 }}>IMPORT ANOTHER</PillButton>
        </div>
      ) : !preview ? (
        <>
          <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="flex flex-col items-center justify-center font-mono transition-colors hover:border-black"
            style={{ gap: 8, minHeight: 180, border: '2px dashed #D1D5DB', borderRadius: 22, background: INSET, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            <span style={lbl(12, '.16em', 800)}>{busy ? 'READING…' : 'PICK A FILE'}</span>
            <span style={{ fontSize: 11, color: MUTED }}>A CSV or an .xlsx. One row per person, with an email or a phone number.</span>
          </button>
        </>
      ) : (
        <>
          <span className="flex items-center" style={{ gap: 10, padding: '10px 10px 10px 16px', borderRadius: 999, background: INSET }}>
            <span className="min-w-0 flex-1 truncate" style={{ fontSize: 12, fontWeight: 700 }}>{file?.name}</span>
            <span style={{ fontSize: 10, color: MUTED, whiteSpace: 'nowrap' }}>{Number(preview.rowCount).toLocaleString()} ROWS · {cols.length} COLUMNS{preview.hadHeader ? '' : ' · NO HEADER'}</span>
            <PillButton variant="ghost" size={32} onClick={reset} disabled={busy}>PICK ANOTHER FILE</PillButton>
          </span>

          <div className="flex flex-col" style={{ gap: 8 }} data-testid="import-mapping">
            <span style={{ ...lbl(10, '.16em'), color: MUTED }}>WHICH COLUMN IS WHICH</span>
            <div className="flex flex-col" style={{ gap: 6 }}>
              {FIELDS.map((f) => (
                <div key={f.key} className="flex flex-wrap items-center" style={{ gap: 6, padding: '8px 8px 8px 16px', borderRadius: 22, background: INSET }} role="radiogroup" aria-label={`${f.label} column`} data-testid={`map-${f.key}`}>
                  <span style={{ width: 96, flexShrink: 0, ...lbl(10, '.12em') }}>{f.label}</span>
                  <button type="button" role="radio" aria-checked={mapping[f.key] === null} onClick={() => setMap(f.key, null)} style={chip(mapping[f.key] === null)}>NONE</button>
                  {cols.map((c) => <button key={c.index} type="button" role="radio" aria-checked={mapping[f.key] === c.index} onClick={() => setMap(f.key, c.index)} style={chip(mapping[f.key] === c.index)}>{c.header}</button>)}
                </div>
              ))}
            </div>
            {needContact && <span style={{ fontSize: 11, color: RED }}>Pick an email or a phone column. One of the two is needed.</span>}
            <label className="flex items-center self-start" style={{ gap: 10, padding: '6px 6px 6px 16px', borderRadius: 999, background: INSET }}>
              <span style={lbl(10, '.12em')}>NUMBERS ARE</span>
              <select value={dial} onChange={(e) => setDial(e.target.value)} aria-label="Default country code" style={{ height: 36, padding: '0 12px', border: 'none', borderRadius: 999, background: '#fff', fontFamily: 'inherit', fontSize: 12 }}>
                <option value="+1">+1 · CANADA / US</option>
                <option value="+44">+44 · UK</option>
                <option value="+61">+61 · AUSTRALIA</option>
                <option value="+52">+52 · MEXICO</option>
                <option value="+33">+33 · FRANCE</option>
                <option value="+49">+49 · GERMANY</option>
              </select>
            </label>
          </div>

          <div style={{ overflowX: 'auto', borderRadius: 16, background: INSET, padding: 6 }} data-testid="import-sample">
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, cols.length)}, minmax(120px, 1fr))`, gap: 2, minWidth: Math.max(1, cols.length) * 124, fontSize: 11 }}>
              {cols.map((c) => <span key={c.index} style={{ padding: '8px 10px', whiteSpace: 'nowrap', fontSize: 10, fontWeight: 700, letterSpacing: '.06em', background: mapped.has(c.index) ? INK : '#fff', color: mapped.has(c.index) ? '#fff' : INK, borderRadius: 10 }}>{c.header}</span>)}
              {(preview.sample || []).flatMap((row, i) => row.map((cell, j) => <span key={`${i}-${j}`} style={{ padding: '7px 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cell || '—'}</span>))}
            </div>
          </div>

          <div className="flex flex-col" style={{ gap: 8 }}>
            <span style={{ ...lbl(10, '.16em'), color: MUTED }}>PUT THEM IN A GROUP</span>
            <span className="flex flex-wrap" style={{ gap: 6 }} data-testid="import-group">
              {[{ id: 'none', name: 'NO GROUP' }, ...groups, { id: 'new', name: '+ NEW GROUP' }].map((g) => {
                const on = groupChoice === g.id;
                return <button key={g.id} type="button" aria-pressed={on} onClick={() => setGroupChoice(g.id)} style={{ ...chip(on), height: 34, padding: '0 14px', background: on ? INK : CHIP, letterSpacing: '.1em' }}>{String(g.name).toUpperCase()}</button>;
              })}
            </span>
            {groupChoice === 'new' && <input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} maxLength={60} placeholder="Group name" aria-label="New group name" style={{ height: 44, padding: '0 18px', border: 'none', borderRadius: 999, background: INSET, fontFamily: 'inherit', fontSize: 13, outline: 'none' }} />}
          </div>

          <div onClick={() => setConsent((c) => !c)} className="flex items-start text-left font-mono" style={{ gap: 12, padding: '14px 16px', borderRadius: 18, background: INSET, cursor: 'pointer' }} data-testid="import-consent" data-on={consent ? '1' : '0'}>
            <RoundCheck on={consent} onChange={setConsent} label="These people gave me permission to contact them" />
            <span style={{ fontSize: 12, lineHeight: 1.6 }}><b>These people gave me permission to contact them.</b> They asked to hear from {ownerType === 'club' ? 'the club' : 'us'}, signed up at an event, or are on a list I already email or text. Every email carries an unsubscribe link and every text honours STOP.</span>
          </div>

          <span className="flex flex-wrap items-center" style={{ gap: 10 }}>
            {consent && <span style={{ fontSize: 11, color: GREEN }}>Rows already on the list are merged, not doubled.</span>}
            <PillButton size={48} onClick={commit} disabled={!canCommit} style={{ marginLeft: 'auto', opacity: canCommit ? 1 : 0.35 }} data-testid="import-commit">{busy ? 'IMPORTING…' : `IMPORT ${Number(preview.rowCount).toLocaleString()} ROWS`}</PillButton>
          </span>
        </>
      )}
    </ClubPanel>
  );
}
